import ProductWeight from "@/components/admin/ProductWeight";
import { sellingUnits, shippingComplete, shippingSetupMessage } from "@/lib/productShipping";
import { translate, useLang } from "@/i18n";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Field as BaseField, inputCls } from "@/components/admin/FormField";
import ImageUpload from "@/components/admin/ImageUpload";

const KNOWN_TAGS = [
  { value: "featured", label: "Featured" },
  { value: "bestseller", label: "Bestseller" },
  { value: "new", label: "New Arrival" },
  { value: "special", label: "Special Offer" },
  { value: "pwodwi-lakay", label: "Pwodwi Lakay" },
];

const emptyProduct = {
  selling_unit: "", selling_unit_label_en: "", selling_unit_label_fr: "",
  name_en: "", name_fr: "", sku: "", origin: "Ayiti", weight: "",
  price: "", sale_price: "", stock: 0, category_slug: "",
  images: [], description_en: "", description_fr: "", ingredients_fr: "",
  tags: [], made_in_haiti: true, active: true,
  shipping_weight: "", weight_unit: "lb", package_length: "", package_width: "", package_height: "",
  dimension_unit: "in", requires_shipping: true, ships_separately: false, shipping_class: "standard",
};

function generateSKU() {
  const rand = () => Math.random().toString(36).slice(2, 6).toUpperCase();
  return `KB-${rand()}-${rand()}`;
}

const labels = {'Basic Information':'Produit', 'Name (EN)':'Nom (EN)', 'Name (FR)':'Nom (FR)',
  'Origin':'Origine', 'Weight / Size':'Libellé commercial du poids / format', 'SALES INFORMATION':'Unité vendue',
  'Selling unit':'Unité vendue', 'Customer presentation (FR)':'Libellé client (FR)', 'Customer presentation (EN)':'Libellé client (EN)',
  'SHIPPING INFORMATION':'Poids et livraison', 'Pricing':'Prix', 'Price ($)':'Prix (USD)', 'Sale Price ($)':'Prix promotionnel (USD)',
  'Inventory':'Stock', 'Stock Quantity':'Quantité en stock', 'Category':'Catégorie', 'Product Images':'Photos',
  'Description':'Description', 'Ingredients (FR)':'Ingrédients (FR)', 'Status':'Publication', 'Visibility':'Visibilité',
  'Length':'Longueur', 'Width':'Largeur', 'Height':'Hauteur', 'Dimension unit':'Unité des dimensions', 'Shipping class':'Précautions d’expédition'};
function Field({ label, children }) {
  const {lang} = useLang();
  return <BaseField label={lang === 'fr' ? labels[label] || label : label}>{children}</BaseField>;
}
function Section({ title, children }) {
  const {lang} = useLang();
  return (
    <section className="rounded-2xl border border-border bg-white p-6 card-shadow">
      <h2 className="mb-4 font-head text-lg font-bold text-navy">{lang === 'fr' ? labels[title] || title : title}</h2>
      {children}
    </section>
  );
}

export default function ProductForm() {
  const { lang } = useLang();
  const L = (fr, en) => lang === "fr" ? fr : en;
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(isEdit ? null : { ...emptyProduct });
  const [confirmMeasurements, setConfirmMeasurements] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/products/${id}`)
      .then((r) => {
        const p = r.data.product;
        setForm({
          ...emptyProduct, ...p,
          price: p.price ?? "", sale_price: p.sale_price ?? "",
          images: p.images || [], tags: p.tags || [],
        });
      })
      .catch(() => { toast.error("Product not found"); navigate("/admin/products"); });
  }, [id, isEdit, navigate]);

  const set = (patch) => {
    if (["packing_rule", "selling_unit", "shipping_weight", "weight_unit", "package_length", "package_width", "package_height", "dimension_unit", "requires_shipping", "ships_separately", "shipping_class"].some(k => k in patch)) {
      patch.automatic_shipping_eligible = false;
      setConfirmMeasurements(false); patch = {...patch, shipping_measurements_verified: false};
    }
    setForm(f => ({...f, ...patch}));
  };
  const toggleTag = (tag) => set({ tags: form.tags.includes(tag) ? form.tags.filter((t) => t !== tag) : [...form.tags, tag] });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form, confirm_shipping_measurements: confirmMeasurements,
      price: parseFloat(form.price) || 0,
      sale_price: form.sale_price !== "" && form.sale_price !== null ? parseFloat(form.sale_price) : null,
      stock: parseInt(form.stock, 10) || 0,
    };
    try {
      if (isEdit) await api.put(`/admin/products/${id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      navigate("/admin/products");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="flex items-center gap-2 text-navy"><Loader2 className="animate-spin" size={18} /> Loading product…</div>;
  }

  return (
    <div data-testid="admin-product-form">
      <div className="mb-6 flex items-center gap-3">
        <Link to="/admin/products" className="text-navy/50 hover:text-navy" aria-label="Back to products"><ArrowLeft size={20} /></Link>
        <h1 className="font-display text-3xl font-bold text-navy">{isEdit ? L('Modifier le produit', 'Edit Product') : L('Nouveau produit', 'New Product')}</h1>
      </div>

      <form onSubmit={save} className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Section title="Basic Information">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name (EN)"><input required value={form.name_en} onChange={(e) => set({ name_en: e.target.value })} className={inputCls} /></Field>
              <Field label="Name (FR)"><input required value={form.name_fr} onChange={(e) => set({ name_fr: e.target.value })} className={inputCls} /></Field>
            </div>
            <details className="mt-4"><summary>{lang === "fr" ? "Options commerciales" : "Merchandising options"}</summary>
              <Field label="SKU">
                <div className="flex gap-2">
                  <input value={form.sku} onChange={(e) => set({ sku: e.target.value })} className={inputCls} placeholder="KB-XXXX-XXXX" />
                  <button type="button" onClick={() => set({ sku: generateSKU() })} className="whitespace-nowrap rounded-lg border border-border px-3 text-xs font-semibold text-navy hover:border-gold">{L("Générer", "Generate")}</button>
                </div>
              </Field>
              <Field label="Origin"><input value={form.origin} onChange={(e) => set({ origin: e.target.value })} className={inputCls} /></Field>
              <Field label="Weight / Size"><input value={form.weight} onChange={(e) => set({ weight: e.target.value })} className={inputCls} /></Field>
            <div className="mt-4">
              <span className="mb-2 block text-xs font-medium text-navy/60">Tags</span>
              <div className="flex flex-wrap gap-3">
                {KNOWN_TAGS.map((t) => (
                  <label key={t.value} className="flex items-center gap-1.5 text-sm text-navy">
                    <input type="checkbox" checked={form.tags.includes(t.value)} onChange={() => toggleTag(t.value)} className="accent-gold" /> {t.label}
                  </label>
                ))}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-navy">
                <input type="checkbox" checked={form.made_in_haiti} onChange={(e) => set({ made_in_haiti: e.target.checked })} className="accent-gold" /> 100% Made in Haiti
              </label>
            </div>
            </details>
          </Section>

          <Section title="SALES INFORMATION">
            <Field label="Selling unit"><select data-testid="selling-unit-select" className={inputCls} value={form.selling_unit || ""} onChange={e => set({selling_unit:e.target.value, selling_unit_label_en:e.target.value ? translate(`selling.sold.${e.target.value}`, "en") : "", selling_unit_label_fr:e.target.value ? translate(`selling.sold.${e.target.value}`, "fr") : ""})}>
              <option value="">{L('Choisir l’unité vendue', 'Choose the selling unit')}</option>{sellingUnits.map(unit => <option key={unit} value={unit}>{translate(`selling.unit.${unit}`, lang)}</option>)}
            </select></Field>
            <details><summary>{lang === "fr" ? "Personnaliser les libellés" : "Customize labels"}</summary>
            <Field label="Customer presentation (FR)"><input maxLength={120} className={inputCls} value={form.selling_unit_label_fr || ""} onChange={e => set({selling_unit_label_fr:e.target.value})} /></Field>
            <Field label="Customer presentation (EN)"><input maxLength={120} className={inputCls} value={form.selling_unit_label_en || ""} onChange={e => set({selling_unit_label_en:e.target.value})} /></Field>
            </details>
            <p className="mt-2 text-sm text-navy/60">{L('Le prix, le stock et le poids concernent une unité vendue, pas nécessairement le sac visible en photo.', 'Price, stock and weight refer to one selling unit, not necessarily the supplier bag in the photo.')}</p>
          </Section>
          <Section title="SHIPPING INFORMATION">
            {form.requires_shipping !== false && <><ProductWeight form={form} set={set} />
            <label className="my-3 flex min-h-11 items-start gap-3 text-sm"><input data-testid="automatic-product" type="checkbox" checked={!!form.automatic_shipping_eligible} onChange={e=>set({automatic_shipping_eligible:e.target.checked})}/>{L('J’ai pesé cet article. Il n’est pas volumineux et peut être envoyé avec les autres produits aux tarifs validés de la boutique.', 'I weighed this item. It is not bulky and can be shipped with other products at the store’s approved prices.')}</label>
            </>}
            {form.requires_shipping === false && <p>{L('Ce produit ne nécessite pas d’expédition. Aucun poids à renseigner.', 'This product does not require shipping. No weight is needed.')}</p>}
            <details data-testid="product-shipping-advanced"><summary className="cursor-pointer font-semibold">{L('Dimensions et emballage particulier', 'Dimensions and special packaging')}</summary>
            <p className="my-3" data-testid="shipping-data-status">{shippingSetupMessage(form,lang)} <Link className="underline" to="/admin/shipping">{L('Gérer les emballages', 'Manage packaging')}</Link></p>
            {!form.shipping_measurements_verified && shippingComplete(form) && form.selling_unit && <label className="mb-3 flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={confirmMeasurements} onChange={e => setConfirmMeasurements(e.target.checked)} />{L('Je confirme le poids mesuré et l’emballage testé pour ce produit.', 'I confirm the measured weight and tested packaging for this product.')}</label>}
            <div className="mb-4 space-y-2" data-testid="product-packing-rule">
              <Field label={lang === "fr" ? "Règle d’emballage vérifiée (facultative)" : "Verified packing rule (optional)"}>
                <select className={inputCls} value={form.packing_rule?.package || ""} onChange={e => set({packing_rule: e.target.value ? {package: e.target.value, max_units: null} : null})}>
                  <option value="">{lang === "fr" ? "Carton personnalisé — dimensions requises" : "Custom box — dimensions required"}</option>
                  <option value="SmallFlatRateBox">USPS Small Flat Rate Box</option><option value="LargeFlatRateBox">USPS Large Flat Rate Box</option>
                </select>
              </Field>
              {form.packing_rule && <Field label={lang === "fr" ? "Quantité maximale testée dans ce carton (même produit)" : "Maximum tested units in this box (same product)"}>
                <input type="number" min="1" max="100" step="1" required className={inputCls} value={form.packing_rule.max_units ?? ""} onChange={e => set({packing_rule: {...form.packing_rule, max_units: e.target.value ? Number(e.target.value) : null}})} />
              </Field>}
              <p className="text-xs">{lang === "fr" ? "Utilisez réellement ce carton et le calage validé. Les produits différents ne sont pas mélangés. Toute modification impose une nouvelle vérification." : "Use this actual box and verified padding. Different products are not mixed. Changes require renewed verification."}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {[["package_length", "Length"], ["package_width", "Width"], ["package_height", "Height"]].map(([key, label]) => <Field key={key} label={label}><input type="number" step="0.001" min="0.001" value={form[key] ?? ""} onChange={(e) => set({ [key]: e.target.value })} className={inputCls} /></Field>)}
              <Field label="Dimension unit"><select className={inputCls} value={form.dimension_unit} onChange={(e) => set({ dimension_unit: e.target.value })}>{["in", "cm"].map((u) => <option key={u}>{u}</option>)}</select></Field>
              <Field label="Shipping class"><select className={inputCls} value={form.shipping_class} onChange={(e) => set({ shipping_class: e.target.value })}><option value="standard">{L("Produit standard", "Standard rectangular parcel")}</option><option value="nonstandard">{L("Produit particulier (liquide, verre, etc.)", "Nonstandard contents (e.g. qualifying liquids / glass)")}</option></select></Field>
            </div>
            {[["requires_shipping", L("Produit à expédier", "Requires shipping")], ["ships_separately", L("Un colis par unité", "Ship each unit separately")]].map(([key, label]) => <label key={key} className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={form[key]} onChange={(e) => set({ [key]: e.target.checked })} />{label}</label>)}
            </details>
          </Section>

          <Section title="Pricing">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Price ($)"><input type="number" step="0.01" min="0" required value={form.price} onChange={(e) => set({ price: e.target.value })} className={inputCls} /></Field>
              <Field label="Sale Price ($)"><input type="number" step="0.01" min="0" value={form.sale_price ?? ""} onChange={(e) => set({ sale_price: e.target.value })} className={inputCls} placeholder="Optional" /></Field>
            </div>
          </Section>

          <Section title="Inventory">
            <Field label="Stock Quantity"><input type="number" min="0" value={form.stock} onChange={(e) => set({ stock: e.target.value })} className={inputCls} /></Field>
          </Section>

          <Section title="Category">
            <Field label="Category">
              <select required value={form.category_slug} onChange={(e) => set({ category_slug: e.target.value })} className={inputCls}>
                <option value="">{L('Choisir une catégorie', 'Select a category')}</option>
                {cats.map((c) => <option key={c.id} value={c.slug}>{c[`name_${lang}`] || c.name_en}</option>)}
              </select>
            </Field>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Product Images">
            <ImageUpload images={form.images} onChange={(images) => set({ images })} />
          </Section>

          <Section title="Description">
            <div className="space-y-3">
              <Field label="Description (EN)"><textarea value={form.description_en} onChange={(e) => set({ description_en: e.target.value })} className={inputCls} rows={4} /></Field>
              <Field label="Description (FR)"><textarea value={form.description_fr} onChange={(e) => set({ description_fr: e.target.value })} className={inputCls} rows={4} /></Field>
              <Field label="Ingredients (FR)"><textarea value={form.ingredients_fr} onChange={(e) => set({ ingredients_fr: e.target.value })} className={inputCls} rows={2} /></Field>
            </div>
          </Section>

          <Section title="Status">
            <Field label="Visibility">
              <select value={form.active ? "active" : "inactive"} onChange={(e) => set({ active: e.target.value === "active" })} className={inputCls}>
                <option value="active">{L('Visible dans la boutique', 'Active — visible in the shop')}</option>
                <option value="inactive">{L('Masqué dans la boutique', 'Inactive — hidden from the shop')}</option>
              </select>
            </Field>
          </Section>

          <button type="submit" disabled={saving} className="btn-gold flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold disabled:opacity-60" data-testid="product-save">
            {saving && <Loader2 size={16} className="animate-spin" />} {isEdit ? L('Enregistrer', 'Save Changes') : L('Créer le produit', 'Create Product')}
          </button>
        </div>
      </form>
    </div>
  );
}
